import { useState, useEffect } from 'react';
import { Home, Image as ImageIcon, Utensils, Info, Zap, Save, History, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '../../components/ui/button';
import { InlineText, InlineImage, InlineArray, SectionCard as InlineSectionCard } from '../../components/cms/InlineEditors';
import RichTextEditor from '../../components/cms/RichTextEditor';
import { VersionHistoryViewer } from '../../components/cms/VersionHistoryViewer';
import versionHistoryService from '../../services/versionHistoryService';
import { authService } from '../../services/authService';
import adminApi from '../../services/adminApi';
import cmsApiService from '../../services/cmsApiService';

// Types
interface HeroSlide {
  id?: number;
  subtitle: string;
  title: string;
  titleLine2: string;
  buttonText: string;
  backgroundImageUrl: string;
  sortOrder: number;
  active: boolean;
  previewImageUrl?: string; 
  [key: string]: unknown;
}

interface CategoryItem {
  id?: number;
  name: string;
  imageUrl: string;
  _isNew?: boolean;      // Track if this is a newly added item
  _isEdited?: boolean;   // Track if this existing item was edited
  [key: string]: unknown;  // Allow indexing for InlineArray compatibility
}

interface ServiceItem {
  id?: number;
  name: string;
  subname: string;
  imageUrl: string;
  _isNew?: boolean;      // Track if this is a newly added service
  _isEdited?: boolean;   // Track if this existing service was edited
  [key: string]: unknown;  // Allow indexing for InlineArray compatibility
}

interface RooftopItem {
  id?: number;
  name: string;
  subName: string;
  imageUrl: string;
  _isNew?: boolean;      // Track if this is a newly added item
  _isEdited?: boolean;   // Track if this existing item was edited
  [key: string]: unknown;  // Allow indexing for InlineArray compatibility
}

interface MenuItem {
  id?: number;
  name: string;
  subname: string;
  imageUrl: string;
  _isNew?: boolean;      // Track if this is a newly added menu item
  _isEdited?: boolean;   // Track if this existing menu item was edited
  [key: string]: unknown;  // Allow indexing for InlineArray compatibility
}

interface PageContentSection {
  id?: number;
  pageName: string;
  sectionName: string;
  attributes: Record<string, unknown>;
}

// Helper function to read file as data URL
const readFileAsDataURL = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export default function NewHomePageManager() {
  // State
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingHeroSlider, setIsSavingHeroSlider] = useState(false);
  const [isSavingCategorySection, setIsSavingCategorySection] = useState(false);
  const [isSavingAboutSection, setIsSavingAboutSection] = useState(false);
  const [isSavingMenuShowcase, setIsSavingMenuShowcase] = useState(false);
  const [isSavingCtaSection, setIsSavingCtaSection] = useState(false);
  const [historyDialog, setHistoryDialog] = useState<{ type: string; id: number } | null>(null);

  // Section 1: Hero Slider - starts empty, loaded from API
  const [heroSlides, setHeroSlides] = useState<HeroSlide[]>([]);

  // Section 2: Category Section
  const [categorySection, setCategorySection] = useState<PageContentSection>({
    pageName: 'home',
    sectionName: 'HOME',
    attributes: {
      subtitle: 'What We Offer',
      title: 'Our Specialties',
      description: 'Discover authentic Sri Lankan Pittu varieties...',
      buttonText: 'View Full Menu',
      items: [
        
      ] as CategoryItem[],
    },
  });
  
  // Track pending file uploads for category items
  
  // Track pending file uploads for hero sliders (key: tempId, value: File) for new sliders
  const [pendingHeroSliderFiles, setPendingHeroSliderFiles] = useState<Record<string, File>>({});

  // Section 3: Video Player
  const [videoSection, setVideoSection] = useState<PageContentSection>({
    pageName: 'home',
    sectionName: 'HOME',
    attributes: {
      youtubeUrl: '',
      title: 'Grand Pittu Experience',
    },
  });

  // Section 4: Menu Showcase
  const [menuShowcaseSection, setMenuShowcaseSection] = useState<PageContentSection>({
    pageName: 'home',
    sectionName: 'HOME',
    attributes: {
      subtitle: 'Our Menu',
      title: 'Featured Dishes',
      buttonText: '',  // Empty string for menu showcase
      items: [] as MenuItem[],
    },
  });

  // Section 5: About Section
  const [aboutSection, setAboutSection] = useState<PageContentSection>({
    pageName: 'home',
    sectionName: 'HOME',
    attributes: {
      subtitle: 'About Us',
      title: 'Our Story',
      description: 'Experience authentic Sri Lankan cuisine with a modern twist. Our journey began with a passion for traditional flavors.',
      backgroundImageUrl: '',
      buttonText: 'Learn More',
      services: [] as ServiceItem[],
    },
  });

  // Section 6: Testimonials (managed separately, just display link)
  // Section 7: Rooftop-CTA Section
  const [ctaSection, setCtaSection] = useState<PageContentSection>({
    pageName: 'home',
    sectionName: 'HOME',
    attributes: {
      subtitle: 'Visit Us',
      title: 'Experience Our Rooftop Dining',
      description: 'Enjoy breathtaking views while savoring our authentic cuisine',
      buttonText: 'Reserve Now',
      backgroundImageUrl: '',
      rooftopItems: [] as RooftopItem[],
    },
  });

  // Load data from API
  useEffect(() => {
    loadPageData();
  }, []);

  const loadPageData = async () => {
    setIsLoading(true);
    try {
      // Load hero sliders
      try {
        const heroResponse = await adminApi.fetch<any[]>('/hero-sliders');
      
      if (heroResponse && heroResponse.length > 0) {
        const mappedSliders = heroResponse.map((slide: any) => ({
          id: slide.id,
          subtitle: slide.subtitle || '',
          title: slide.title || '',
          titleLine2: slide.titleLine2 || '',
          buttonText: slide.buttonText || 'Explore Menu',
          backgroundImageUrl: slide.backgroundImageUrl || '',
          sortOrder: slide.sortOrder || 1,
          active: slide.active !== false,
        }));
        setHeroSlides(mappedSliders);
      } else {
        toast.error(` No hero sliders found, using defaults`);
      }
    } catch (error) {
      toast.error(`Failed to load hero sliders`);
      // Keep default values, don't fail entire load
    }

    // Load category section - fetch all and use the first one (HOME page typically has one)
    try {
      const allSections = await cmsApiService.getAllCategorySections();
      const catData = Array.isArray(allSections) && allSections.length > 0 ? allSections[0] : null;
      
      if (catData?.id) {
        setCategorySection(prev => ({
          ...prev,
          id: catData.id,
          attributes: {
            ...prev.attributes,
            subtitle: catData.subtitle || 'What We Offer',
            title: catData.title || 'Our Specialties',
            description: catData.description || '',
            buttonText: catData.buttonText || 'View Full Menu',
            items: (catData.items || []).map((item: any) => ({
              id: item.id,
              name: item.name || '',
              imageUrl: item.imageUrl || '',
            })),
          },
        }));
      } else {
        toast.error(`Category section not configured yet - using defaults`);
      }
    } catch {
      // Silently use defaults - category section may not exist yet
      toast.error(`Category section not available - using defaults`);
    }

    // Load Menu Showcase Section
    try {
      const menuData = await cmsApiService.getMenuShowcaseByName('HOME');
      
      if (menuData?.id) {
        setMenuShowcaseSection(prev => ({
          ...prev,
          id: menuData.id,
          attributes: {
            ...prev.attributes,
            subtitle: menuData.subtitle || 'Our Menu',
            title: menuData.title || 'Featured Dishes',
            items: (menuData.images || []).map((item: any) => ({
              id: item.id,
              name: item.name || '',
              subname: item.subName || '',
              imageUrl: item.imageUrl || '',
            })),
          },
        }));
      } else {
        toast.error(`Menu showcase not configured yet - using defaults`);
      }
    } catch {
      // Silently use defaults - menu showMenu showcase not available - using defaultscase may not exist yet
      toast.error(`Menu showcase not available - using defaults`);
    }

    // Load About Section (includes Video Player data and About data)
    try {
      const aboutData = await cmsApiService.getAboutSectionByName('HOME');
      
      if (aboutData?.id) {
        // Map video fields to videoSection
        setVideoSection(prev => ({
          ...prev,
          id: aboutData.id,
          attributes: {
            ...prev.attributes,
            youtubeUrl: aboutData.videoUrl || '',
            title: aboutData.videoTitle || 'Grand Pittu Experience',
          },
        }));
        
        // Map services/images from backend
        const mappedServices = (aboutData.images || []).map((img: any) => ({
          id: img.id,
          name: img.name || '',
          subname: img.subName || '',
          imageUrl: img.imageUrl || '',
        }));
        
        // Map about fields to aboutSection
        setAboutSection(prev => ({
          ...prev,
          id: aboutData.id,
          attributes: {
            ...prev.attributes,
            subtitle: aboutData.subTitle || 'About Us',
            title: aboutData.title || 'Our Story',
            description: aboutData.description || '',
            backgroundImageUrl: aboutData.bgImageUrl || '',
            buttonText: aboutData.buttonName || 'Explore',
            services: mappedServices,
          },
        }));
        const servicesCount = (aboutData.images || []).length;
      } else {
        toast.error(`About section not configured - section will need to be created`);
      }
    } catch (error) {
      
    }

    // Load CTA/Rooftop Section
    try {
      const rooftopData = await cmsApiService.getRooftopSectionByPage('HOME');
      
      if (rooftopData?.id) {
        setCtaSection(prev => ({
          ...prev,
          id: rooftopData.id,
          attributes: {
            ...prev.attributes,
            // For CTA/Rooftop section, data is in main fields, not cta* fields
            subtitle: rooftopData.subTitle || 'Experience',
            title: rooftopData.title || 'Our Rooftop Lounge',
            description: rooftopData.description || '',
            buttonText: rooftopData.buttonName || 'Explore Rooftop',
            backgroundImageUrl: rooftopData.imgUrl || '',
            rooftopItems: (rooftopData.items || []).map((item: any) => ({
              id: item.id,
              name: item.name,
              subName: item.subName || '',
              imageUrl: item.imageUrl,
            })),
          },
        }));
      } else {
        toast.error(` No hero sliders found, using defaults`);
      }
    } catch (error) {
      
      setCtaSection(prev => ({
        ...prev,
        id: undefined, // Make sure ID is undefined so "Create" button shows
        attributes: {
          ...prev.attributes,
          rooftopItems: [], // Initialize with empty array
        },
      }));
    }

    } catch (error) {
      toast.error('Failed to load page data');
    } finally {
      setIsLoading(false);
    }
  };

  // Save functions
  // Save Hero Slides - POST for new, PUT for existing
  // Save a single hero slider - for immediate save on add/edit
  const saveSingleHeroSlider = async (slide: HeroSlide) => {
    setIsSavingHeroSlider(true);
    try {
      const dto = {
        subtitle: slide.subtitle || '',
        title: slide.title || '',
        titleLine2: slide.titleLine2 || '',
        buttonText: slide.buttonText || '',
        sortOrder: slide.sortOrder || 1,
        active: slide.active ?? true,
      };
      
      if (slide.id) {
        // Existing slide - use PUT (image already updated if changed)
        await cmsApiService.updateHeroSlider(slide.id, dto);
        toast.success('Hero slide updated successfully');
      } else {
        // New slide - retrieve the file using tempFileId
        const tempFileId = (slide as any).tempFileId;
        const imageFile = tempFileId ? pendingHeroSliderFiles[tempFileId] : null;
        
        if (!imageFile) {
          toast.error('New sliders require an image. Please upload an image first.');
          return;
        }
        
        // Send file with POST request
        await cmsApiService.addHeroSlider(dto, imageFile);
        toast.success('Hero slide created successfully');
        
        // Clear the pending file for this slider
        if (tempFileId) {
          setPendingHeroSliderFiles(prev => {
            const newFiles = { ...prev };
            delete newFiles[tempFileId];
            return newFiles;
          });
        }
        
        // Reload from API to get the new slide with ID
        await loadPageData();
      }
    } catch (error) {
      toast.error('Failed to save hero slider');
    } finally {
      setIsSavingHeroSlider(false);
    }
  };

  // Handle hero slider reordering
  const handleHeroSliderReorder = async () => {
    try {
      // Extract IDs in current order
      const orderedIds = heroSlides
        .filter(slide => slide.id) // Only include saved sliders with IDs
        .map(slide => slide.id as number);
      
      if (orderedIds.length === 0) {
        toast.error('No saved sliders to reorder');
        return;
      }
      
      await cmsApiService.reorderHeroSliders(orderedIds);
      toast.success('Hero slides reordered successfully');
    } catch (error) {
      toast.error('Failed to reorder hero slides');
    }
  };

  // Delete hero slider
  const deleteHeroSlider = async (slide: HeroSlide, index: number): Promise<boolean> => {
    try {
      if (slide.id) {
        // If slide has ID, delete from backend
        await cmsApiService.deleteHeroSlider(slide.id);
        toast.success('Slide deleted successfully');
        return true;
      } else {
        // If no ID, just remove from local state (unsaved slide)
        toast.success('Slide removed');
        return true;
      }
    } catch (error) {
      toast.error('Failed to delete slide');
      return false;
    }
  };

  // Save category section metadata + items (for bulk operations like section save button)
  // Helper to create category section
  const createCategorySection = async (dto: any) => {
    const result = await cmsApiService.addCategorySection(dto);
    
    if (result) {
      const allSections = await cmsApiService.getAllCategorySections();
      const catData = Array.isArray(allSections) && allSections.length > 0 
        ? allSections.at(-1)
        : null;
        
      if (catData?.id) {
        setCategorySection(prev => ({ ...prev, id: catData.id }));
        return { id: catData.id };
      }
    }
    return result;
  };

  // Helper to update category section
  const updateCategorySection = async (dto: any) => {
    return await cmsApiService.updateCategorySection(categorySection.id!, dto);
  };

  const saveCategorySection = async () => {
    setIsSavingCategorySection(true);
    try {
      const isCreate = !categorySection.id;
      
      // Build the DTO - section metadata only (items handled separately)
      const dto = {
        subtitle: categorySection.attributes.subtitle as string || '',
        title: categorySection.attributes.title as string || '',
        description: categorySection.attributes.description as string || '',
        buttonText: categorySection.attributes.buttonText as string || '',
        aboutSectionImagesDetails: undefined, // Omit items for create, managed separately
      };
      
      // Call appropriate helper based on create/update
      const result = isCreate ? await createCategorySection(dto) : await updateCategorySection(dto);
      
      // Track version history
      const user = await authService.getProfile();
      const entityId = String(categorySection.id || result?.id || 'new');
      versionHistoryService.track({
        entityType: 'category-section',
        entityId: entityId,
        action: categorySection.id ? 'update' : 'create',
        changes: { categorySection: { old: {}, new: dto } },
        snapshot: dto as Record<string, unknown>,
        user: user?.name || 'Unknown',
      });

      // Save category items via dedicated endpoints for existing section
      if (categorySection.id) {
        await saveCategoryItemFeatures();
      }
      
      const actionText = categorySection.id ? 'updated' : 'created';
      toast.success(`Category section ${actionText} successfully`);
      
      // Reload data to get fresh state
      await loadPageData();
    } catch (error) {
      toast.error('Failed to save category section');
    } finally {
      setIsSavingCategorySection(false);
    }
  };

  // Save category item features: new items via add-images, edited items via features/{id}
  const saveCategoryItemFeatures = async (): Promise<boolean> => {
    if (!categorySection.id) {
      return true;
    }

    const currentItems = (categorySection.attributes.items as CategoryItem[]) || [];
    
    // Separate new items (no id or marked as new) from edited existing items
    const newItems = currentItems.filter(i => !i.id || i._isNew);
    const editedItems = currentItems.filter(i => i.id && i._isEdited && !i._isNew);

    try {
      // 1. Add new items via PUT /category-section/add-images/{id}
      if (newItems.length > 0) {
        const addImagesDto = newItems.map(i => ({
          name: i.name,
          subName: i.name, // Use name as subName
          imageUrl: i.imageUrl
        }));
                
        await adminApi.fetch(`/category-section/add-images/${categorySection.id}`, {
          method: 'PUT',
          data: addImagesDto,
        });
        
        toast.success(`Added ${newItems.length} new category item(s)`);
      }

      // 2. Update edited items via PUT /category-section/features/{id}
      for (const item of editedItems) {
        const updateDto = {
          id: item.id,
          name: item.name,
          subName: item.name, // Use name as subName
          imageUrl: item.imageUrl
        };
        
        
        await adminApi.fetch(`/category-section/features/${item.id}`, {
          method: 'PUT',
          data: updateDto,
        });
      }
      
      if (editedItems.length > 0) {
        toast.success(`Updated ${editedItems.length} category item(s)`);
      }

      return true;
    } catch (error) {
      toast.error('Failed to save category items');
      return false;
    }
  };

  // Delete category item
  const deleteCategoryItem = async (item: CategoryItem, index: number): Promise<boolean> => {
    if (!(item as any).id) {
      // If no ID, just remove from local state
      toast.success('Item removed');
      return true;
    }

    try {
      await cmsApiService.deleteCategorySectionFeature((item as any).id);
      toast.success('Category item deleted successfully');
      return true;
    } catch (error) {
      toast.error('Failed to delete category item');
      return false;
    }
  };

  // Save About Section - main section metadata only (items saved individually)
  const saveAboutSection = async () => {
    setIsSavingAboutSection(true);
    try {
      const isCreate = !aboutSection.id;
      
      // Must send complete DTO - backend expects uppercase sectionName to match GET by-name
      // Note: Use undefined to omit optional fields from JSON (backend has database constraints)
      const baseDto = {
        sectionName: 'HOME',  // Must match getAboutSectionByName('HOME')
        title: aboutSection.attributes.title as string || 'Our Story',
        subTitle: aboutSection.attributes.subtitle as string || 'About Us',
        description: aboutSection.attributes.description as string || 'Experience authentic cuisine',
        bgImageUrl: aboutSection.attributes.backgroundImageUrl as string || undefined,
        buttonName: aboutSection.attributes.buttonText as string || 'Learn More',
        videoUrl: (videoSection.attributes.youtubeUrl as string) || undefined,
        videoTitle: videoSection.attributes.title as string || 'Grand Pittu Experience',
        ctaSubTitle: undefined,  // HOME page doesn't use CTA section - omit from JSON
        ctaTitle: undefined,     
        ctaButtonName: undefined,  
        ctaBgImageUrl: undefined,
      };

      // Include images array for create, exclude for update (managed separately)
      // Note: Backend builder pattern issue - send null instead of empty array
      const dto = aboutSection.id ? baseDto : {
        ...baseDto,
        images: undefined, // Omit images field to avoid backend builder null issue
      };
      
      
      let result: any;
      if (aboutSection.id) {
        result = await cmsApiService.updateAboutSection(aboutSection.id, dto);
      } else {
        result = await cmsApiService.addAboutSection(dto);
        // Update state with new ID
        if (result?.id) {
          setAboutSection(prev => ({ ...prev, id: result.id }));
          setVideoSection(prev => ({ ...prev, id: result.id }));
        }
      }
      
      // Track version history
      const user = await authService.getProfile();
      const entityId = String(aboutSection.id || result?.id || 'new');
      versionHistoryService.track({
        entityType: 'about-section',
        entityId: entityId,
        action: aboutSection.id ? 'update' : 'create',
        changes: { about: { old: {}, new: dto } },
        snapshot: dto as Record<string, unknown>,
        user: user?.name || 'Unknown',
      });

      // Save service features via dedicated endpoints for existing section
      if (aboutSection.id) {
        await saveServiceFeatures();
      }
      
      const actionText = aboutSection.id ? 'updated' : 'created';
      toast.success(`About section ${actionText} successfully`);
      
      // Reload data to get fresh state
      await loadPageData();
    } catch (error) {
      toast.error('Failed to save about section');
    } finally {
      setIsSavingAboutSection(false);
    }
  };

  // Save service features: new services via add-images, edited services via features/{id}
  const saveServiceFeatures = async (): Promise<boolean> => {
    if (!aboutSection.id) {
      return true;
    }

    const currentServices = (aboutSection.attributes.services as ServiceItem[]) || [];
    
    // Separate new services (no id or marked as new) from edited existing services
    const newServices = currentServices.filter(s => !s.id || s._isNew);
    const editedServices = currentServices.filter(s => s.id && s._isEdited && !s._isNew);
    

    try {
      // 1. Add new services via PUT /about-section/add-images/{id}
      if (newServices.length > 0) {
        const addImagesDto = newServices.map(s => ({
          name: s.name,
          subName: s.subname,
          imageUrl: s.imageUrl
        }));
        
        
        await adminApi.fetch(`/about-section/add-images/${aboutSection.id}`, {
          method: 'PUT',
          data: addImagesDto,
        });
        
        toast.success(`Added ${newServices.length} new service(s)`);
      }

      // 2. Update edited services via PUT /about-section/features/{id}
      for (const service of editedServices) {
        const updateDto = {
          id: service.id,
          name: service.name,
          subName: service.subname,
          imageUrl: service.imageUrl
        };
        
        
        await adminApi.fetch(`/about-section/features/${service.id}`, {
          method: 'PUT',
          data: updateDto,
        });
      }
      
      if (editedServices.length > 0) {
        toast.success(`Updated ${editedServices.length} service(s)`);
      }

      return true;
    } catch (error) {
      toast.error('Failed to save services');
      return false;
    }
  };

  // Handler for service changes - marks new items as _isNew and edited items as _isEdited
  const handleServicesChange = (items: ServiceItem[]) => {
    
    // Get current services to compare
    const currentServices = (aboutSection.attributes.services as ServiceItem[]) || [];
    
    // Process items to mark them appropriately
    const processedItems = items.map(item => {
      // If item has no id, it's a new item
      if (!item.id) {
        return { ...item, _isNew: true };
      }
      
      // If item has an id, check if it was edited
      const existingItem = currentServices.find(s => s.id === item.id);
      if (existingItem) {
        const isEdited = existingItem.name !== item.name || 
                         existingItem.subname !== item.subname || 
                         existingItem.imageUrl !== item.imageUrl;
        if (isEdited) {
          return { ...item, _isEdited: true };
        }
      }
      
      return item;
    });
    
    setAboutSection(prev => ({
      ...prev,
      attributes: { ...prev.attributes, services: processedItems },
    }));
    
  };

  // Helper to save individual about section field (like background image)
  const saveAboutSectionField = async (
    fieldOverrides?: Record<string, unknown>
  ) => {
    if (!aboutSection.id) {
      return;
    }
    
    try {
      const currentAttrs = { ...aboutSection.attributes, ...fieldOverrides };
      
      const dto = {
        sectionName: 'HOME',
        title: currentAttrs.title as string || '',
        subTitle: currentAttrs.subtitle as string || '',
        description: currentAttrs.description as string || '',
        bgImageUrl: currentAttrs.backgroundImageUrl as string || '',
        buttonName: currentAttrs.buttonText as string || '',
        videoUrl: videoSection.attributes.youtubeUrl as string || '',
        videoTitle: videoSection.attributes.title as string || '',
        ctaSubTitle: '',
        ctaTitle: '',
        ctaButtonName: '',
        ctaBgImageUrl: '',
        images: [],
      };
      
      await cmsApiService.updateAboutSection(aboutSection.id, dto);
      toast.success('Changes saved!');
    } catch (error) {
      toast.error('Failed to save changes');
    }
  };

  // Delete service item
  const deleteServiceItem = async (item: ServiceItem, index: number): Promise<boolean> => {
    if (!item.id) {
      toast.success('Item removed');
      return true;
    }
    
    try {
      await cmsApiService.deleteAboutSectionImageDetails(item.id);
      toast.success('Service item deleted successfully');
      
      // Reload to refresh the list
      await loadPageData();
      return true;
    } catch (error) {
      toast.error('Failed to delete service item');
      return false;
    }
  };

  // Save CTA/Rooftop Section - main section metadata AND items
  const saveCtaSection = async (backgroundImageUrlOverride?: string) => {
    setIsSavingCtaSection(true);
    try {
      const isCreate = !ctaSection.id;
      
      // Use override if provided (for immediate save after upload), otherwise use state
      const backgroundImageUrl = backgroundImageUrlOverride || (ctaSection.attributes.backgroundImageUrl as string) || '';
      
      // For CTA/Rooftop section: main fields contain CTA data, cta* fields are empty
      const dto = {
        pageName: 'HOME',
        title: ctaSection.attributes.title as string || '',
        subTitle: ctaSection.attributes.subtitle as string || '',
        description: ctaSection.attributes.description as string || '',
        buttonName: ctaSection.attributes.buttonText as string || '',
        imgUrl: backgroundImageUrl,
        contactSubTitle: '',  
        contactTitle: '',     
        ctaTitle: '',         
        ctaSubTitle: '',      
        ctaDescription: '',   
        ctaButtonText: '',    
        ctaImgUrl: '',        
        items: [],
      };
      
      
      let result: any;
      if (ctaSection.id) {
        result = await cmsApiService.updateRooftopSection(ctaSection.id, dto);
      } else {
        result = await cmsApiService.addRooftopSection(dto);
        // Update state with new ID
        if (result?.id) {
          setCtaSection(prev => ({ ...prev, id: result.id }));
        }
      }
      
      // Save rooftop items (new and edited) only for existing sections
      if (ctaSection.id) {
        const itemsSaveSuccess = await saveRooftopItems();
        
      }
      
      // Track version history
      const user = await authService.getProfile();
      const entityId = String(ctaSection.id || result?.id || 'new');
      versionHistoryService.track({
        entityType: 'rooftop-section',
        entityId: entityId,
        action: ctaSection.id ? 'update' : 'create',
        changes: { cta: { old: {}, new: dto } },
        snapshot: dto as Record<string, unknown>,
        user: user?.name || 'Unknown',
      });
      
      const actionText = ctaSection.id ? 'updated' : 'created';
      toast.success(`CTA/Rooftop section ${actionText} successfully`);
      
      // Reload data to get fresh state
      await loadPageData();
    } catch (error) {
      toast.error('Failed to save CTA section');
    } finally {
      setIsSavingCtaSection(false);
    }
  };

  // Save rooftop items (new and edited)
  const saveRooftopItems = async (): Promise<boolean> => {
    if (!ctaSection.id) {
      toast.error('Please create the CTA section first before adding items');
      return false;
    }

    const rooftopItems = (ctaSection.attributes.rooftopItems as RooftopItem[]) || [];
    const newItems = rooftopItems.filter(item => item._isNew);
    const editedItems = rooftopItems.filter(item => item._isEdited && item.id);


    try {
      // 1. Add new items via PUT /rooftop-items/add-images/{id}
      if (newItems.length > 0) {
        const addImagesDto = newItems.map(item => ({
          name: item.name,
          subName: item.subName,
          imageUrl: item.imageUrl
        }));
        
        
        await adminApi.fetch(`/rooftop-items/add-images/${ctaSection.id}`, {
          method: 'PUT',
          data: addImagesDto,
        });
        
        toast.success(`Added ${newItems.length} new rooftop item(s)`);
      }

      // 2. Update edited items via PUT /rooftop-items/features/{id}
      for (const item of editedItems) {
        const updateDto = {
          id: item.id,
          name: item.name,
          subName: item.subName,
          imageUrl: item.imageUrl
        };
        
        
        await adminApi.fetch(`/rooftop-items/features/${item.id}`, {
          method: 'PUT',
          data: updateDto,
        });
      }
      
      if (editedItems.length > 0) {
        toast.success(`Updated ${editedItems.length} rooftop item(s)`);
      }

      return true;
    } catch (error) {
      toast.error('Failed to save rooftop items');
      return false;
    }
  };

  // Handler for rooftop items changes - marks new items as _isNew and edited items as _isEdited
  const handleRooftopItemsChange = (items: RooftopItem[]) => {
    
    // Get current items to compare
    const currentItems = (ctaSection.attributes.rooftopItems as RooftopItem[]) || [];
    
    // Process items to mark them appropriately
    const processedItems = items.map(item => {
      // If item has no id, it's a new item
      if (!item.id) {
        return { ...item, _isNew: true };
      }
      
      // If item has an id, check if it was edited
      const existingItem = currentItems.find(ri => ri.id === item.id);
      if (existingItem) {
        const isEdited = existingItem.name !== item.name || 
                         existingItem.subName !== item.subName || 
                         existingItem.imageUrl !== item.imageUrl;
        if (isEdited) {
          return { ...item, _isEdited: true };
        }
      }
      
      return item;
    });
    
    setCtaSection(prev => ({
      ...prev,
      attributes: { ...prev.attributes, rooftopItems: processedItems },
    }));
    
  };

  // Delete rooftop item
  const deleteRooftopItem = async (item: RooftopItem, index: number): Promise<boolean> => {
    if (!item.id) {
      toast.success('Item removed');
      return true;
    }
    
    try {
      await cmsApiService.deleteRooftopImageDetails(item.id);
      toast.success('Rooftop item deleted successfully');
      
      // Reload to refresh the list
      await loadPageData();
      return true;
    } catch (error) {
      toast.error('Failed to delete rooftop item');
      return false;
    }
  };

  // Image upload handler using cmsApiService (for About, Rooftop sections)
  const handleImageUpload = async (file: File): Promise<string> => {
    try {
      const response = await cmsApiService.uploadImage(file, 'home');
      return response.url || response.imageUrl || response;
    } catch (error) {
      toast.error('Failed to upload image');
      throw error;
    }
  };

  // Rooftop/CTA section background image upload handler
  const handleRooftopImageUpload = async (file: File): Promise<string> => {
    try {
      const response = await cmsApiService.uploadImage(file, 'rooftop-section');
      const imageUrl = response.url || response.imageUrl || response;
      
      if (!imageUrl || typeof imageUrl !== 'string') {
        throw new Error('No URL returned from upload');
      }
      
      toast.success('Image uploaded successfully');
      return imageUrl;
    } catch (error) {
      toast.error('Failed to upload image');
      throw error;
    }
  };

  // Category item image upload handler - uploads to S3 and returns URL (like about section)
  const handleCategoryItemImageUpload = async (file: File): Promise<string> => {
    try {
      const response = await cmsApiService.uploadImage(file, 'category-section');
      const imageUrl = response.url || response.imageUrl || response;
      
      if (!imageUrl || typeof imageUrl !== 'string') {
        throw new Error('No URL returned from upload');
      }
      
      toast.success('Image uploaded successfully');
      return imageUrl;
    } catch (error) {
      toast.error('Failed to upload image');
      throw error;
    }
  };

  // Handler for category item changes - marks new items as _isNew and edited items as _isEdited
  const handleCategoryItemsChange = (items: CategoryItem[]) => {
    
    // Get current category items to compare
    const currentItems = (categorySection.attributes.items as CategoryItem[]) || [];
    
    // Process items to mark them appropriately
    const processedItems = items.map(item => {
      // If item has no id, it's a new item
      if (!item.id) {
        return { ...item, _isNew: true };
      }
      
      // If item has an id, check if it was edited
      const existingItem = currentItems.find(i => i.id === item.id);
      if (existingItem) {
        const isEdited = existingItem.name !== item.name || 
                         existingItem.imageUrl !== item.imageUrl;
        if (isEdited) {
          return { ...item, _isEdited: true };
        }
      }
      
      return item;
    });
    
    setCategorySection(prev => ({
      ...prev,
      attributes: {
        ...prev.attributes,
        items: processedItems,
      },
    }));
    
  };

  // ==================== MENU SHOWCASE SECTION ====================

  // Upload a single menu item image to S3 and get URL
  const handleMenuItemImageUpload = async (file: File): Promise<string> => {
    try {
      const response = await cmsApiService.uploadImage(file, 'menu-showcase');
      const imageUrl = response.url || response.imageUrl || response;
      
      if (!imageUrl || typeof imageUrl !== 'string') {
        throw new Error('No URL returned from upload');
      }
      
      toast.success('Image uploaded successfully');
      return imageUrl;
    } catch (error) {
      toast.error('Failed to upload image');
      throw error;
    }
  };

  // Handler for menu item changes - marks new items as _isNew and edited items as _isEdited
  const handleMenuItemsChange = (items: MenuItem[]) => {
    
    // Get current menu items to compare
    const currentItems = (menuShowcaseSection.attributes.items as MenuItem[]) || [];
    
    // Process items to mark them appropriately
    const processedItems = items.map(item => {
      // If item has no id, it's a new item
      if (!item.id) {
        return { ...item, _isNew: true };
      }
      
      // If item has an id, check if it was edited
      const existingItem = currentItems.find(i => i.id === item.id);
      if (existingItem) {
        const isEdited = existingItem.name !== item.name || 
                         existingItem.subname !== item.subname || 
                         existingItem.imageUrl !== item.imageUrl;
        if (isEdited) {
          return { ...item, _isEdited: true };
        }
      }
      
      return item;
    });
    
    setMenuShowcaseSection(prev => ({
      ...prev,
      attributes: {
        ...prev.attributes,
        items: processedItems,
      },
    }));
    
  };

  // Save menu showcase section metadata + items
  const saveMenuShowcase = async () => {
    setIsSavingMenuShowcase(true);
    try {
      const isCreate = !menuShowcaseSection.id;
      
      // Build the DTO - section metadata only (items handled separately)
      const dto = {
        sectionName: 'HOME',
        title: menuShowcaseSection.attributes.title as string || '',
        subtitle: menuShowcaseSection.attributes.subtitle as string || '',
        buttonText: '', // Empty string for menu showcase
        pdfUrl: '', // Empty string for menu showcase
        pdfFilePath: '', // Empty string for menu showcase
        images: [], // Empty array for create, items added separately after section is created
      };

      
      let result: any;
      if (menuShowcaseSection.id) {
        // UPDATE existing menu showcase via PUT /menu-cards/{id}
        result = await cmsApiService.updateMenuShowcase(menuShowcaseSection.id, dto);
      } else {
        // CREATE new menu showcase via POST /menu-page
        result = await cmsApiService.addMenuShowcase(dto);
        
        // Fetch the newly created section
        if (result?.id) {
          setMenuShowcaseSection(prev => ({ ...prev, id: result.id }));
        }
      }
      
      // Track version history
      const user = await authService.getProfile();
      const entityId = String(menuShowcaseSection.id || result?.id || 'new');
      versionHistoryService.track({
        entityType: 'menu-showcase',
        entityId: entityId,
        action: menuShowcaseSection.id ? 'update' : 'create',
        changes: { menuShowcase: { old: {}, new: dto } },
        snapshot: dto as Record<string, unknown>,
        user: user?.name || 'Unknown',
      });

      // Save menu items via dedicated endpoints for existing section
      if (menuShowcaseSection.id) {
        await saveMenuItemFeatures();
      }
      
      const actionText = menuShowcaseSection.id ? 'updated' : 'created';
      toast.success(`Menu showcase ${actionText} successfully`);
      
      // Reload data to get fresh state
      await loadPageData();
    } catch (error) {
      toast.error('Failed to save menu showcase');
    } finally {
      setIsSavingMenuShowcase(false);
    }
  };

  // Save menu item features: new items via add-images, edited items via features/{id}
  const saveMenuItemFeatures = async (): Promise<boolean> => {
    if (!menuShowcaseSection.id) {
      return true;
    }

    const currentItems = (menuShowcaseSection.attributes.items as MenuItem[]) || [];
    
    // Separate new items (no id or marked as new) from edited existing items
    const newItems = currentItems.filter(i => !i.id || i._isNew);
    const editedItems = currentItems.filter(i => i.id && i._isEdited && !i._isNew);


    try {
      // 1. Add new items via PUT /menu-cards/add-images/{id}
      if (newItems.length > 0) {
        const addItemsDto = newItems.map(i => ({
          name: i.name,
          subName: i.subname,
          imageUrl: i.imageUrl
        }));
                
        await adminApi.fetch(`/menu-cards/add-images/${menuShowcaseSection.id}`, {
          method: 'PUT',
          data: addItemsDto,
        });
        
        toast.success(`Added ${newItems.length} new menu item(s)`);
      }

      // 2. Update edited items via PUT /menu-cards/features/{id}
      for (const item of editedItems) {
        const updateDto = {
          id: item.id,
          name: item.name,
          subName: item.subname,
          imageUrl: item.imageUrl
        };
                
        await adminApi.fetch(`/menu-cards/features/${item.id}`, {
          method: 'PUT',
          data: updateDto,
        });
      }
      
      if (editedItems.length > 0) {
        toast.success(`Updated ${editedItems.length} menu item(s)`);
      }

      return true;
    } catch (error) {
      toast.error('Failed to save menu items');
      return false;
    }
  };

  // Delete menu item
  const deleteMenuItem = async (item: MenuItem, index: number): Promise<boolean> => {
    if (!item.id) {
      // If no ID, just remove from local state
      toast.success('Item removed');
      return true;
    }

    try {
      await cmsApiService.deleteMenuShowcaseItem(item.id);
      toast.success('Menu item deleted successfully');
      return true;
    } catch (error) {
      toast.error('Failed to delete menu item');
      return false;
    }
  };

  return (
    <div className="space-y-6 p-6">
      {/* Page Header */}
      <div className="flex items-center justify-between backdrop-blur-sm bg-white/80 dark:bg-slate-900/80 rounded-2xl p-6 border-2 border-slate-200 dark:border-slate-800 shadow-sm">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-700 dark:from-white dark:via-slate-100 dark:to-slate-300 bg-clip-text text-transparent">
            <Home className="w-8 h-8 text-slate-700 dark:text-slate-300" />
            Home Page Manager
          </h1>
        </div>
        <Button variant="outline" onClick={loadPageData} disabled={isLoading} className="border-2 border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800">
          <History className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Clock className="w-8 h-8 animate-spin mx-auto mb-2" />
            <p>Loading home page...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Section 1: Hero Slider */}
          <InlineSectionCard
            title="Section 1: Hero Slider"
            description="Main carousel slides on homepage"
            icon={<ImageIcon className="w-5 h-5" />}
            onViewHistory={() => setHistoryDialog({ type: 'hero-slider', id: 1 })}
          >
        <InlineArray
          items={heroSlides}
          onChange={setHeroSlides}
          onSave={handleHeroSliderReorder}
          onSaveItem={saveSingleHeroSlider}
          onDeleteItem={deleteHeroSlider}
          label="Slides"
          canReorder={true}
          getDefaultItem={() => ({
            subtitle: '',
            title: '',
            titleLine2: '',
            buttonText: 'Explore',
            backgroundImageUrl: '',
            previewImageUrl: '', // For data URL preview
            sortOrder: heroSlides.length + 1,
            active: true,
          })}
          renderItem={(slide, index) => (
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded overflow-hidden flex-shrink-0">
                {((slide as any).previewImageUrl || slide.backgroundImageUrl) ? (
                  <img src={String((slide as any).previewImageUrl || slide.backgroundImageUrl)} alt={String(slide.title)} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-muted flex items-center justify-center">
                    <ImageIcon className="w-8 h-8 text-muted-foreground" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <div className="font-medium">{slide.subtitle} {slide.title}</div>
                {slide.titleLine2 && <div className="text-sm text-muted-foreground">{slide.titleLine2}</div>}
                <div className="text-xs text-muted-foreground mt-1">
                  Button text: "{slide.buttonText}" • {slide.active ? <span className="text-green-600">✓ Active</span> : <span className="text-red-600">✗ Inactive</span>}
                </div>
              </div>
            </div>
          )}
          renderForm={(slide, onChange, onSave, onCancel) => (
            <div className="space-y-4">
              <InlineText
                label="Subtitle"
                value={(slide.subtitle as string) || ''}
                onChange={(v) => onChange('subtitle', v)}
                placeholder="Welcome To"
              />
              <InlineText
                label="Title"
                value={(slide.title as string) || ''}
                onChange={(v) => onChange('title', v)}
                placeholder="Grand Pittu"
              />
              <InlineText
                label="Title Line 2"
                value={(slide.titleLine2 as string) || ''}
                onChange={(v) => onChange('titleLine2', v)}
                placeholder="Restaurant"
              />
              <InlineText
                label="Button Text"
                value={(slide.buttonText as string) || ''}
                onChange={(v) => onChange('buttonText', v)}
                placeholder="Explore Menu"
              />
              <InlineImage
                label="Background Image"
                value={((slide as any).previewImageUrl || slide.backgroundImageUrl) as string || ''}
                onChange={(v) => onChange('backgroundImageUrl', v)}
                onUpload={async (file: File) => {
                  if ((slide as HeroSlide).id) {
                    // Existing slide - send file with update request
                    const result = await cmsApiService.updateHeroSlider((slide as HeroSlide).id!, {
                      subtitle: slide.subtitle as string || '',
                      title: slide.title as string || '',
                      titleLine2: slide.titleLine2 as string || '',
                      buttonText: slide.buttonText as string || '',
                      sortOrder: (slide as HeroSlide).sortOrder || 1,
                      active: (slide as HeroSlide).active ?? true,
                    }, file);
                    onChange('backgroundImageUrl', result.backgroundImageUrl);
                    toast.success('Image updated successfully');
                    return result.backgroundImageUrl;
                  } else {
                    // New slide - store file temporarily, will be sent on save
                    // Get the current index in the editing context
                    const tempId = `temp-${Date.now()}`;
                    setPendingHeroSliderFiles(prev => ({ ...prev, [tempId]: file }));
                    // Store tempId in the slide so we can retrieve the file later
                    onChange('tempFileId', tempId);
                    // Create a data URL for preview ONLY - store in separate field
                    const dataUrl = await readFileAsDataURL(file);
                    onChange('previewImageUrl', dataUrl);
                    toast.success('Image selected. Click "Save" to upload.');
                    return dataUrl;
                  }
                }}
              />
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={(slide.active as boolean) || false}
                  onChange={(e) => onChange('active', e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm">Active</span>
              </div>
              <div className="flex gap-2">
                <Button onClick={onSave} size="sm" variant="blueBg">
                  <Save className="w-3 h-3 mr-1" /> Save
                </Button>
                <Button onClick={onCancel} variant="outline" size="sm">
                  Cancel
                </Button>
              </div>
            </div>
          )}
        />
        {heroSlides.length > 1 && (
          <div className="mt-4 flex justify-end">
            <Button 
              onClick={handleHeroSliderReorder} 
              variant="blueBg" 
              size="sm"
              disabled={isSavingHeroSlider}
            >
              <Save className="w-3 h-3 mr-1" /> 
              Save Slide Order
            </Button>
          </div>
        )}
      </InlineSectionCard>

      {/* Section 2: Category Section */}
      <InlineSectionCard
        title="Section 2: Category Section"
        description="What We Offer - Specialty items grid"
        icon={<Utensils className="w-5 h-5" />}
        onViewHistory={() => setHistoryDialog({ type: 'page-content', id: 2 })}
      >
        <InlineText
          label="Subtitle"
          value={(categorySection.attributes.subtitle as string) || ''}
          onChange={(v) => {
            setCategorySection((prev) => ({
              ...prev,
              attributes: { ...prev.attributes, subtitle: v },
            }));
          }}
        />
        <InlineText
          label="Title"
          value={(categorySection.attributes.title as string) || ''}
          onChange={(v) => {
            setCategorySection((prev) => ({
              ...prev,
              attributes: { ...prev.attributes, title: v },
            }));
          }}
        />
        <div className="space-y-2">
          <div className="text-sm font-medium">Description</div>
          <RichTextEditor
            value={(categorySection.attributes.description as string) || ''}
            onChange={(v) => {
              setCategorySection((prev) => ({
                ...prev,
                attributes: { ...prev.attributes, description: v },
              }));
            }}
            placeholder="Write category section description..."
            minHeight="140px"
          />
        </div>
        <InlineText
          label="Button Text"
          value={(categorySection.attributes.buttonText as string) || ''}
          onChange={(v) => {
            setCategorySection((prev) => ({
              ...prev,
              attributes: { ...prev.attributes, buttonText: v },
            }));
          }}
        />

        {/* Category Items - InlineArray with individual image per item */}
        <div className="space-y-2">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium">Category Items</div>
            {!categorySection.id && (
              <span className="text-xs text-muted-foreground">
                💡 Save section first to add items
              </span>
            )}
          </div>
          <InlineArray
            items={categorySection.attributes.items as CategoryItem[]}
            onChange={handleCategoryItemsChange}
            onDeleteItem={deleteCategoryItem}
            label=""
            canAdd={!!categorySection.id}
            canDelete={true}
            canReorder={false}
            getDefaultItem={() => ({
              name: '',
              imageUrl: '',
              _isNew: true,
            })}
            renderItem={(item) => (
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded overflow-hidden flex-shrink-0">
                  {item.imageUrl ? (
                    <img src={item.imageUrl} alt={item.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-muted flex items-center justify-center">
                      <ImageIcon className="w-5 h-5 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <div>
                  <div className="font-medium">{item.name || 'Unnamed Item'}</div>
                  <div className="text-xs text-muted-foreground">
                    {item.imageUrl ? ' ' : 'No image'}
                  </div>
                </div>
              </div>
            )}
            renderForm={(item, onChange, onSave, onCancel) => (
              <div className="space-y-3">
                <InlineText
                  label="Name"
                  value={(item.name as string) || ''}
                  onChange={(v) => { onChange('name', v);  }}
                />
                <InlineImage
                  label="Image"
                  value={(item.imageUrl as string) || ''}
                  onChange={(v) => { onChange('imageUrl', v);  }}
                  onUpload={handleCategoryItemImageUpload}
                />
                <div className="flex gap-2">
                  <Button type="button" onClick={onSave} size="sm" variant="blueBg">Done</Button>
                  <Button type="button" onClick={onCancel} variant="outline" size="sm">Cancel</Button>
                </div>
              </div>
            )}
          />
        </div>

        {/* Save Section Button */}
        <div className="flex justify-end pt-4 border-t mt-4">
          <Button 
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              saveCategorySection();
            }}
            disabled={isSavingCategorySection}
            className="gap-2"
            variant="blueBg"
          >
            {isSavingCategorySection ? (
              <>Saving...</>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {categorySection.id ? 'Save Category Section' : 'Create Category Section'}
              </>
            )}
          </Button>
        </div>
      </InlineSectionCard>


      {/* Section 4: Menu Showcase */}
      <InlineSectionCard
        title="Section 3: Menu Showcase"
        description="Featured menu items showcased on home page"
        icon={<Utensils className="w-5 h-5" />}
        onViewHistory={() => setHistoryDialog({ type: 'menu-showcase', id: menuShowcaseSection.id || 0 })}
      >
        <InlineText
          label="Subtitle"
          value={(menuShowcaseSection.attributes.subtitle as string) || ''}
          onChange={(v) => {
            setMenuShowcaseSection((prev) => ({
              ...prev,
              attributes: { ...prev.attributes, subtitle: v },
            }));
            
          }}
        />
        <InlineText
          label="Title"
          value={(menuShowcaseSection.attributes.title as string) || ''}
          onChange={(v) => {
            setMenuShowcaseSection((prev) => ({
              ...prev,
              attributes: { ...prev.attributes, title: v },
            }));
            
          }}
        />

        <div className="space-y-2">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium">Menu Items</div>
            {!menuShowcaseSection.id && (
              <span className="text-xs text-muted-foreground">
                Save section first to add items
              </span>
            )}
          </div>
          <InlineArray
            items={menuShowcaseSection.attributes.items as MenuItem[]}
            onChange={handleMenuItemsChange}
            onDeleteItem={deleteMenuItem}
            label=""
            canAdd={!!menuShowcaseSection.id}
            canDelete={true}
            canReorder={false}
            getDefaultItem={() => ({
              name: '',
              subname: '',
              imageUrl: '',
              _isNew: true,
            })}
            renderItem={(item) => (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded overflow-hidden">
                  {item.imageUrl ? (
                    <img src={item.imageUrl} alt={item.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-muted flex items-center justify-center">
                      <ImageIcon className="w-4 h-4 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <div>
                  <div className="font-medium">{item.name}</div>
                  <div className="text-sm text-muted-foreground">{item.subname}</div>
                </div>
              </div>
            )}
            renderForm={(item, onChange, onSave, onCancel) => (
              <div className="space-y-3">
                <InlineText
                  label="Name"
                  value={(item.name as string) || ''}
                  onChange={(v) => { onChange('name', v);  }}
                />
                <InlineText
                  label="Subname"
                  value={(item.subname as string) || ''}
                  onChange={(v) => { onChange('subname', v);  }}
                />
                <InlineImage
                  label="Image"
                  value={(item.imageUrl as string) || ''}
                  onChange={(v) => { onChange('imageUrl', v);  }}
                  onUpload={handleMenuItemImageUpload}
                />
                <div className="flex gap-2">
                  <Button type="button" onClick={onSave} size="sm" variant="blueBg">Done</Button>
                  <Button type="button" onClick={onCancel} variant="outline" size="sm">Cancel</Button>
                </div>
              </div>
            )}
          />
        </div>

        {/* Save Section Button */}
        <div className="flex justify-end pt-4 border-t mt-4">
          <Button 
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              saveMenuShowcase();
            }}
            disabled={isSavingMenuShowcase}
            className="gap-2"
            variant="blueBg"
          >
            {isSavingMenuShowcase ? (
              <>Saving...</>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {menuShowcaseSection.id ? 'Save Menu Showcase' : 'Create Menu Showcase'}
              </>
            )}
          </Button>
        </div>
      </InlineSectionCard>

      {/* Section 5: About Section */}
      <InlineSectionCard
        title="Section 4: About Section (Services)"
        description="About us with service items"
        icon={<Info className="w-5 h-5" />}
        onViewHistory={() => setHistoryDialog({ type: 'page-content', id: 5 })}
      >
        <InlineText
          label="Subtitle"
          value={(aboutSection.attributes.subtitle as string) || ''}
          onChange={(v) => {
            setAboutSection((prev) => ({
              ...prev,
              attributes: { ...prev.attributes, subtitle: v },
            }));
            
          }}
        />
        <InlineText
          label="Title"
          value={(aboutSection.attributes.title as string) || ''}
          onChange={(v) => {
            setAboutSection((prev) => ({
              ...prev,
              attributes: { ...prev.attributes, title: v },
            }));
            
          }}
        />
        <div className="space-y-2">
          <div className="text-sm font-medium">Description</div>
          <RichTextEditor
            value={(aboutSection.attributes.description as string) || ''}
            onChange={(v) => {
              setAboutSection((prev) => ({
                ...prev,
                attributes: { ...prev.attributes, description: v },
              }));
            }}
            placeholder="Write about section description..."
            minHeight="160px"
          />
        </div>
        <InlineImage
          label="Background Image (About Section)"
          value={(aboutSection.attributes.backgroundImageUrl as string) || ''}
          onChange={(v) => {
            setAboutSection((prev) => ({
              ...prev,
              attributes: { ...prev.attributes, backgroundImageUrl: v },
            }));
            
          }}
          onSave={(newUrl) => {
            if (newUrl) {
              saveAboutSectionField({ backgroundImageUrl: newUrl });
            }
          }}
          onUpload={handleImageUpload}
        />
        <InlineText
          label="Button Text"
          value={(aboutSection.attributes.buttonText as string) || ''}
          onChange={(v) => {
            setAboutSection((prev) => ({
              ...prev,
              attributes: { ...prev.attributes, buttonText: v },
            }));
            
          }}
        />

        <div className="space-y-2">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium">Services</div>
            {!aboutSection.id && (
              <span className="text-xs text-muted-foreground">
                Save section first to add services
              </span>
            )}
          </div>
          <InlineArray
            items={aboutSection.attributes.services as ServiceItem[]}
            onChange={handleServicesChange}
            onDeleteItem={deleteServiceItem}
            label=""
            canAdd={!!aboutSection.id}
            canDelete={true}
            canReorder={false}
            getDefaultItem={() => ({
              name: '',
              subname: '',
              imageUrl: '',
              _isNew: true,
            })}
            renderItem={(item) => (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded overflow-hidden">
                  {item.imageUrl ? (
                    <img src={item.imageUrl} alt={item.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-muted flex items-center justify-center">
                      <ImageIcon className="w-4 h-4 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <div>
                  <div className="font-medium">{item.name}</div>
                  <div className="text-sm text-muted-foreground">{item.subname}</div>
                </div>
              </div>
            )}
            renderForm={(item, onChange, onSave, onCancel) => (
              <div className="space-y-3">
                <InlineText
                  label="Name"
                  value={(item.name as string) || ''}
                  onChange={(v) => { onChange('name', v);  }}
                />
                <InlineText
                  label="Subname"
                  value={(item.subname as string) || ''}
                  onChange={(v) => { onChange('subname', v);  }}
                />
                <InlineImage
                  label="Image"
                  value={(item.imageUrl as string) || ''}
                  onChange={(v) => { onChange('imageUrl', v);  }}
                  onUpload={handleImageUpload}
                />
                <div className="flex gap-2">
                  <Button onClick={onSave} size="sm" variant="blueBg">Done</Button>
                  <Button onClick={onCancel} variant="outline" size="sm">Cancel</Button>
                </div>
              </div>
            )}
          />
        </div>

        {/* Save Section Button */}
        <div className="flex justify-end pt-4 border-t mt-4">
          <Button 
            onClick={saveAboutSection} 
            disabled={isSavingAboutSection}
            className="gap-2"
            variant="blueBg"
          >
            {isSavingAboutSection ? (
              <>Saving...</>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {aboutSection.id ? 'Save About Section' : 'Create About Section'}
              </>
            )}
          </Button>
        </div>
      </InlineSectionCard>


      {/* Section 7: CTA / Rooftop Section */}
      <InlineSectionCard
        title="Section 5: CTA / Rooftop Section"
        description="Call-to-action with rooftop items"
        icon={<Zap className="w-5 h-5" />}
        onViewHistory={() => setHistoryDialog({ type: 'page-content', id: 7 })}
      >
        <InlineText
          label="Subtitle"
          value={(ctaSection.attributes.subtitle as string) || ''}
          onChange={(v) => {
            setCtaSection((prev) => ({
              ...prev,
              attributes: { ...prev.attributes, subtitle: v },
            }));
          }}
        />
        <InlineText
          label="Title"
          value={(ctaSection.attributes.title as string) || ''}
          onChange={(v) => {
            setCtaSection((prev) => ({
              ...prev,
              attributes: { ...prev.attributes, title: v },
            }));
          }}
        />
        <div className="space-y-2">
          <div className="text-sm font-medium">Description</div>
          <RichTextEditor
            value={(ctaSection.attributes.description as string) || ''}
            onChange={(v) => {
              setCtaSection((prev) => ({
                ...prev,
                attributes: { ...prev.attributes, description: v },
              }));
            }}
            placeholder="Write CTA / rooftop description..."
            minHeight="140px"
          />
        </div>
        <InlineText
          label="Button Text"
          value={(ctaSection.attributes.buttonText as string) || ''}
          onChange={(v) => {
            setCtaSection((prev) => ({
              ...prev,
              attributes: { ...prev.attributes, buttonText: v },
            }));
          }}
        />
        <InlineImage
          label="Background Image (CTA/Rooftop Section)"
          value={(ctaSection.attributes.backgroundImageUrl as string) || ''}
          onChange={(v) => {
            setCtaSection((prev) => ({
              ...prev,
              attributes: { ...prev.attributes, backgroundImageUrl: v },
            }));
            
          }}
          onSave={(newUrl) => {
            if (newUrl && ctaSection.id) {
              saveCtaSection(newUrl);
            }
          }}
          onUpload={handleRooftopImageUpload}
        />

        <div className="space-y-2">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium">Rooftop Items</div>
            {!ctaSection.id && (
              <span className="text-xs text-muted-foreground">
                Save section first to add items
              </span>
            )}
          </div>
          <InlineArray
            items={ctaSection.attributes.rooftopItems as RooftopItem[]}
            onChange={handleRooftopItemsChange}
            onDeleteItem={deleteRooftopItem}
            label=""
            canAdd={!!ctaSection.id}
            canDelete={true}
            canReorder={false}
            getDefaultItem={() => ({
              name: '',
              subName: '',
              imageUrl: '',
              _isNew: true,
            })}
          renderItem={(item) => (
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded overflow-hidden">
                {item.imageUrl ? (
                  <img src={item.imageUrl} alt={item.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-muted flex items-center justify-center">
                    <ImageIcon className="w-4 h-4 text-muted-foreground" />
                  </div>
                )}
              </div>
              <div>
                <div className="font-medium">{item.name}</div>
                <div className="text-sm text-muted-foreground">{item.subName}</div>
              </div>
            </div>
          )}
          renderForm={(item, onChange, onSave, onCancel) => (
            <div className="space-y-3">
              <InlineText
                label="Name"
                value={(item.name as string) || ''}
                onChange={(v) => onChange('name', v)}
                placeholder="Rooftop Bar"
              />
              <InlineText
                label="Sub Name"
                value={(item.subName as string) || ''}
                onChange={(v) => onChange('subName', v)}
                placeholder="Premium Experience"
              />
              <InlineImage
                label="Image"
                value={(item.imageUrl as string) || ''}
                onChange={(v) => onChange('imageUrl', v)}
                onUpload={handleRooftopImageUpload}
              />
              <div className="flex gap-2">
                <Button onClick={onSave} size="sm">Done</Button>
                <Button onClick={onCancel} variant="outline" size="sm">Cancel</Button>
              </div>
            </div>
          )}
        />
        </div>

        {/* Save Section Button */}
        <div className="flex justify-end pt-4 border-t mt-4">
          <Button 
            onClick={() => saveCtaSection()} 
            disabled={isSavingCtaSection}
            className="gap-2"
            variant="blueBg"
          >
            {isSavingCtaSection ? (
              <>Saving...</>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {ctaSection.id ? 'Save CTA Section' : 'Create CTA Section'}
              </>
            )}
          </Button>
        </div>
      </InlineSectionCard>
        </>
      )}

      {/* Version History Dialog */}
      {historyDialog && (
        <VersionHistoryViewer
          entityType={historyDialog.type}
          entityId={historyDialog.id}
          isOpen={true}
          onClose={() => setHistoryDialog(null)}
        />
      )}
    </div>
  );
}
